import React, { useEffect, useMemo, useState } from 'react';
import HomeIcon from '@mui/icons-material/Home';
import { Box, Button, TextField, Typography, Modal, Switch, Checkbox, IconButton } from '@mui/material';
import SearchableDropdown from '../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import { ConstantActions, ManagementActions } from '../../../Services/_actions';
import { ProfileObject } from '../../../interfaces/Management.interface.ts';
import { useRecoilState, useRecoilValue } from 'recoil';
import { profileObjAtom } from '../../../Services/_states/Management/management.ts';
import { useNavigate, useParams } from 'react-router-dom';
import { userInformationAtom } from '../../../Services/_states/auth.js';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import DynamicBreadcrumbs from '../../Shared/Breadcrumb/index.tsx';

const ObjectLevelPermission: React.FC = () => {
  const [profileObject, setProfileObject] = useRecoilState<ProfileObject>(profileObjAtom);
  const actions = ManagementActions();
  const { showSnackBar } = useSnackBar();
  const { mode } = useParams();
  const navigate = useNavigate();

  // const breadcrumbsData = mode === 'edit'
  // ? [
  //     { label: <IconButton color='primary'><HomeIcon /></IconButton>, onClick: () => window.location.href = "/" },
  //     { label: 'Profile', href: '/r/management/profile-list' },
  //     { label: 'Permission Edit', href: '#', onClick: (event) => { navigate(-1) } },
  //   ]
  // : [
  //     { label: <IconButton color='primary'><HomeIcon /></IconButton>, href: '/' },
  //     { label: 'Users', href: '/r/management/user-list/' },
  //     { label: 'Permission View', href: '#', onClick: (event) => { navigate(-1) } },
  //   ];


  const columns = useMemo<MRT_ColumnDef<ProfileObject>[]>(
    () => [
      {
        accessorKey: 'index',
        header: 'S.No.',
        enableSorting: false,
        size: 100,
        muiTableHeadCellProps: {
          sx: {
            backgroundImage: 'linear-gradient(0deg, #3e59e3 35%, #5f76e8 100%)',
            color: 'white',
            fontWeight: '600',
            fontSize: '14px',
            fontFamily:'system-ui',
            paddingLeft: '20px !important', // header padding here
          },
        },
        muiTableBodyCellProps: {
          sx: {
            backgroundColor: '#fafafa',
            paddingLeft: '20px !important',
          },
        },
        Cell: ({ row }) => {
          const rowIndex = row.index;
          const page = 0; // Fallback to 0 if undefined
          const limit = 10; // Fallback to 10 if undefined
          return page * limit + rowIndex + 1;
        },
      },
      {
        accessorKey: 'name',
        header: 'Name',
        enableSorting: false,
        size: 200,
      },
      {
        accessorKey: 'view',
        header: 'View',
        enableSorting: false,
        size: 200,
        Cell: ({ cell }: { cell: any }) => (
          <Checkbox
            checked={cell.getValue()}
            onChange={(e) => handleCheckboxChange('view', cell.row.original, e.target.checked)}
            disabled={mode === 'view'}

          />
        ),
      },
      {
        accessorKey: 'update',
        header: 'Update',
        enableSorting: false,
        size: 200,
        Cell: ({ cell }: { cell: any }) => (
          <Checkbox
            checked={cell.getValue()}
            onChange={(e) => handleCheckboxChange('update', cell.row.original, e.target.checked)}
            disabled={mode === 'view'}

          />
        ),
      },
      {
        accessorKey: 'create',
        header: 'Create',
        enableSorting: false,
        size: 200,
        Cell: ({ cell }: { cell: any }) => (
          <Checkbox
            checked={cell.getValue()}
            onChange={(e) => handleCheckboxChange('create', cell.row.original, e.target.checked)}
            disabled={mode === 'view'}
          />
        ),
      },
      {
        accessorKey: 'delete',
        header: 'Delete',
        enableSorting: false,
        size: 200,
        Cell: ({ cell }: { cell: any }) => (
          <Checkbox
            checked={cell.getValue()}
            onChange={(e) => handleCheckboxChange('delete', cell.row.original, e.target.checked)}
            disabled={mode === 'view'}
          />
        ),
      },
      {
        accessorKey: 'view_all',
        header: 'View All',
        enableSorting: false,
        size: 200,
        Cell: ({ cell }: { cell: any }) => (
          <Checkbox
            checked={cell.getValue()}
            onChange={(e) => handleCheckboxChange('view_all', cell.row.original, e.target.checked)}
            disabled={mode === 'view'}
          />
        ),
      },
      {
        accessorKey: 'update_all',
        header: 'Update All',
        enableSorting: false,
        size: 100,
        Cell: ({ cell }: { cell: any }) => (
          <Checkbox
            checked={cell?.getValue()}
            onChange={(e) => handleCheckboxChange('update_all', cell.row.original, e.target.checked)}
            disabled={mode === 'view'}
          />
        ),
      },
    ],
    [profileObject?.object_level_permission]
  );

  const handleCheckboxChange = async (columnKey: string, row: ProfileObject, isChecked: boolean) => {
    try {
      // Update the specific permission locally
      const updatedPermissions = profileObject?.object_level_permission?.map((profile: { id?: string }) => {
        if (profile.id === row.id) {
          return {
            ...profile,
            [columnKey]: isChecked,
          };
        }
        return profile;
      });

      // Identify the specific object to update for the API call
      const objToUpdate = updatedPermissions?.find((item) => item?.id === row?.id);
      const res = await actions.updateObjectLevelPermission(objToUpdate, objToUpdate?.id);

      if (res?.status === 200) {
        // Use callback form of setProfileObject to ensure state updates are based on the latest state
        setProfileObject((prevProfileObject) => {
          const newPermissions = prevProfileObject?.object_level_permission?.map((profile: { id?: string }) => {
            if (profile.id === row.id) {
              return {
                ...profile,
                [columnKey]: isChecked,
              };
            }
            return profile;
          });

          return {
            ...prevProfileObject,
            object_level_permission: newPermissions,
          };
        });
        if (isChecked) {
          showSnackBar(`${columnKey} permission for ${row.name} has been checked`, 'success');
        } else {
          showSnackBar(`${columnKey} permission for ${row.name} has been unchecked`, 'success');
        }
      }
    } catch (error) {
      showSnackBar(`Failed to update ${columnKey} permission for ${row.name}`, 'error');
    }
  };


  return (
    <>
      {/* <DynamicBreadcrumbs items={breadcrumbsData} onBreadcrumbClick={(event, item: { label: string, href: string }) => {
        if (item) {
          navigate(item?.href);
        }
      }}
      /> */}
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: '16px' }}
      >
        <Typography variant="h5" fontWeight="bold">
          Object Level Permission
        </Typography>
        <Typography sx={{ display: 'flex', alignItems: 'center', gap: 2 }}></Typography>
      </Box>
      <MaterialReactTable
        columns={columns}
        data={profileObject?.object_level_permission || []}
        muiTableBodyCellProps={{
          sx: (theme) => ({
            backgroundColor:
              theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
          }),
        }}
        muiTableContainerProps={{ sx: { maxHeight: 'calc(100vh - 225px)', width: '100%' } }}
        muiTableHeadCellProps={{
          sx: {
            backgroundImage: 'linear-gradient(0deg, #3e59e3 35%, #5f76e8 100%)',
            color: 'white',
            fontWeight: '600',
            fontSize: '14px',
            fontFamily: 'system-ui',
          },
        }}
        enableTopToolbar={false}
        state={{
          showColumnFilters: false,
          //   isLoading,
        }}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableColumnActions={false}
        enableColumnFilters={false}
        enableHiding={false}
        enableStickyHeader={true}
        // manualSorting={true}
        initialState={{
          density: 'comfortable',
        }}
        enablePagination={false}
        manualPagination={false}
        // onSortingChange={setSorting}
        enableBottomToolbar={false}
      />
    </>
  );
};
export default ObjectLevelPermission;
